/* © CNES */
import moment from 'moment';
import { refMissions } from '../../assets/config/filters/Referenciel';
import {
  Asset,
  CollectionDataModel,
  CollectionDetails,
} from '../../models/CollectionDetails';
import { getProductHREF } from '../explore/LeftSidePanel/Results/utils';
import {
  ACQUISITION_STATION,
  BANDS,
  CLASSIFICATION,
  CLOUD_COVER,
  COUPLING_MODES,
  DATASET,
  DATETIME,
  END_DATE_TIME,
  END_POINT_URL,
  EO_SNOW_COVER,
  GERALD_NAME,
  GRID_CODE,
  IDENTIFIER,
  INSTRUMENT,
  INSTRUMENT_MODE,
  KEYWORDS,
  MISSION,
  MISSION_TAKE_ID,
  NODATA_PIXEL,
  NO_GEOMETRY,
  ORBIT_ABSOLUTE,
  ORBIT_RELATIVE,
  ORBIT_STATE,
  PARAMETER,
  PLATFORM,
  POLARIZATIONS,
  POLITICAL,
  PPARAMETER,
  PROCESSING_CONTEXT,
  PROCESSING_CORRECTION,
  PROCESSING_DATETIME,
  PROCESSING_FACILITY,
  PROCESSING_LEVEL,
  PROCESSING_LINEAGE,
  PROCESSING_VERSION,
  PROCESSING_WEATHERDATA,
  PRODUCT_ACQUISITION_TYPE,
  PRODUCT_TIMELINESS,
  PRODUCT_TIMELINESS_CATEGORY,
  PRODUCT_TYPE,
  PRODUCT_VALIDITY,
  RASTER_SPATIAL_RESOLUTION,
  RESOLUTION_MODE,
  S2_DATASTRIP_ID,
  S2_DATATAKE_ID,
  S2_DATATAKE_TYPE,
  SAR_BEAM_IDS,
  SAR_CENTER_FREQUENCY,
  SAR_FREQUENCY_BAND,
  SAR_LOOKS_AZIMUTH,
  SAR_LOOKS_EQUIVALENT_NUMBER,
  SAR_LOOKS_RANGE,
  SAR_OBSERVATION_DIRECTION,
  SAR_PIXEL_SPACING_AZIMUTH,
  SAR_PIXEL_SPACING_RANGE,
  SAR_RELATIVE_BURST,
  SAR_RESOLUTION_AZIMUTH,
  SAR_RESOLUTION_RANGE,
  SENSOR_ANGLE,
  SENSOR_PITCH,
  SENSOR_ROLL,
  START_DATE_TIME,
  SWATH,
  SWATH_MODE,
  TEMPORAL_RESOLUTION,
  THEMES,
  VERSION,
  VIEW_AZIMUTH,
  VIEW_INCIDENCE_ANGLE,
  VIEW_OFF_NADIR,
  VIEW_SUN_AZIMUTH,
  VIEW_SUN_ELEVATION,
  VISIBILITY,
  WATER_COVER,
} from './Constants';

/**
 * format Product Details.
 * @param data a product.
 * @returns CollectionDetails
 */
export const formatCollectionDetails = (data: any): CollectionDetails => {
  let collectionDetails = {} as CollectionDetails;
  if (data) {
    collectionDetails = {
      id: data.id,
      checksum: getProductHREF(data.assets)
        ?.split('files/')?.[1]
        ?.split('?scope=')?.[0],
      quicklook: Object.values(data.assets as keyof typeof data.assets)
        .filter((el) => el.description.includes('QUICKLOOK_SD'))
        .find((el) => el.title.toLowerCase().includes('rgb'))
        ?.href?.split('/api')[1] ??
        Object.values(data.assets as keyof typeof data.assets)
          .find((el) => el.description.includes('QUICKLOOK_SD'))
          ?.href?.split('/api')[1],
      productName: data.properties[IDENTIFIER],
      thumbnail: Object.values(data.assets as keyof typeof data.assets)
        .find((el) => el.roles.includes('thumbnail'))
        ?.href?.split('/api')[1],
      pyramid: Object.values(data.assets as keyof typeof data.assets)
        .filter((el) => el.description.includes('QUICKLOOK_HD'))
        ?.map((pyramid) => {
          return { key: pyramid.title, url: pyramid.href?.split('/api')[1] };
        }),
      quicklooks: data.collection?.startsWith('TRISHNA_L')
        ? Object.values(data.assets as keyof typeof data.assets)
          .filter((el) => 
            el.description.includes('QUICKLOOK_SD') &&
            (el.title.toLowerCase().includes('tir') || el.title.toLowerCase().includes('rgb'))
          )
          ?.map((quicklooks) => {
            return { key: quicklooks.title, url: quicklooks.href?.split('/api')[1] };
          })
        : [],
      collectionName:
        refMissions.find((el) => el.key.includes(data.collection))?.name ?? '-',
      endpointURL: data.properties[END_POINT_URL],
      href: getProductHREF(data.assets),
      assets: Object.values(data.assets).filter((asset: any) =>
        asset.roles.includes('data')
      ) as Asset[],
      collectionDataModel: transformToCollectionDetails(data),
    };
  }

  return collectionDetails;
};

/**
 * Calculates the total size of assets based on their descriptions.
 *
 * This function extracts the file size from the `description` field of each asset,
 * sums up all the sizes, and returns the total in bytes.
 *
 * @param {object} assets - An object containing asset data, where each asset has a `description` field.
 * @returns {number} The total size of all assets in bytes. Returns `0` if no assets are provided.
 */
const calculateTotalSize = (assets: object) => {
  if (!assets) return 0;

  return Object.values(assets).reduce((total, asset) => {
    const match = asset.description.match(/File size: (\d+) bytes/);
    const fileSize = match ? parseInt(match[1], 10) : 0;
    return total + fileSize;
  }, 0);
};

/**
 * Transforms STAC data into a structured collection details model.
 *
 * This function filters invalid metadata from the input `stacData` and organizes the
 * properties into a structured `CollectionDataModel`, categorizing them under `general`,
 * `productDetails`, `geometry`, `keywords`, and `location` sections.
 *
 * @param {any} stacData - The raw STAC data containing metadata and asset information.
 * @returns {CollectionDataModel} The transformed collection details model with structured metadata.
 */
export const transformToCollectionDetails = (
  stacData: any
): CollectionDataModel => {
  const filteredProperties: Record<string, any> = {};

  // Filtrer les métadonnées invalides
  for (const key in stacData.properties) {
    const value = stacData.properties[key];
    if (
      value !== -1 &&
      value !== null &&
      value !== 'no data' &&
      value !== 'nodata' &&
      value !== '' &&
      !(Array.isArray(value) && value.length === 0) &&
      !(typeof value === 'object' && Object.keys(value).length === 0)
    ) {
      filteredProperties[key] = value;
    }
  }

  return {
    general: {
      collection:
        refMissions.find((el) => el.key.includes(stacData.collection))?.name ??
        '-',
      sensingStart:
        moment(filteredProperties[START_DATE_TIME])
          .utc()
          .format('YYYY-MM-DD HH:mm:ss') ?? '',
      sensingEnd:
        moment(filteredProperties[END_DATE_TIME])
          .utc()
          .format('YYYY-MM-DD HH:mm:ss') ?? '',
      storageMode: stacData?.storage?.mode,
      totalSize: calculateTotalSize(stacData.assets) / 1048576,
    },
    productDetails: {
      ...filteredProperties, // Ajouter tous les attributs dynamiques brutes
      [PLATFORM]: filteredProperties[PLATFORM] ?? null,
      [PARAMETER]: filteredProperties[PARAMETER] ?? null,
      [PPARAMETER]: filteredProperties[PPARAMETER] ?? null,
      [PROCESSING_LEVEL]: filteredProperties[PROCESSING_LEVEL] ?? null,
      [GRID_CODE]: filteredProperties[GRID_CODE] ?? null,
      [PRODUCT_TYPE]: filteredProperties[PRODUCT_TYPE] ?? null,
      [CLOUD_COVER]: filteredProperties[CLOUD_COVER]
        ? `${Math.round(filteredProperties[CLOUD_COVER] * 100) / 100}%`
        : null,
      [EO_SNOW_COVER]: filteredProperties[EO_SNOW_COVER]
        ? `${Math.round(filteredProperties[EO_SNOW_COVER] * 100) / 100}%`
        : null,
      [WATER_COVER]: filteredProperties[WATER_COVER]
        ? `${Math.round(filteredProperties[WATER_COVER] * 100) / 100}%`
        : null,
      [NODATA_PIXEL]: filteredProperties[NODATA_PIXEL]
        ? `${Math.round(filteredProperties[NODATA_PIXEL] * 100) / 100}%`
        : null,
      [ORBIT_STATE]: filteredProperties[ORBIT_STATE] ?? null,
      [ORBIT_ABSOLUTE]: filteredProperties[ORBIT_ABSOLUTE] ?? null,
      [ORBIT_RELATIVE]: filteredProperties[ORBIT_RELATIVE] ?? null,
      [BANDS]: filteredProperties[BANDS] ?? null,
      [S2_DATATAKE_ID]: filteredProperties[S2_DATATAKE_ID] ?? null,
      [S2_DATATAKE_TYPE]: filteredProperties[S2_DATATAKE_TYPE] ?? null,
      [S2_DATASTRIP_ID]: filteredProperties[S2_DATASTRIP_ID] ?? null,
      [VIEW_INCIDENCE_ANGLE]: filteredProperties[VIEW_INCIDENCE_ANGLE] ?? null,
      [VIEW_AZIMUTH]: filteredProperties[VIEW_AZIMUTH] ?? null,
      [VIEW_SUN_AZIMUTH]: filteredProperties[VIEW_SUN_AZIMUTH] ?? null,
      [VIEW_SUN_ELEVATION]: filteredProperties[VIEW_SUN_ELEVATION] ?? null,
      [VIEW_OFF_NADIR]: filteredProperties[VIEW_OFF_NADIR] ?? null,
      [SWATH]: filteredProperties[SWATH] ?? null,
      [SWATH_MODE]: filteredProperties[SWATH_MODE] ?? null,
      [DATETIME]:
        moment(filteredProperties[DATETIME])
          .utc()
          .format('YYYY-MM-DD HH:mm:ss') ?? null,
      [PRODUCT_TIMELINESS]: filteredProperties[PRODUCT_TIMELINESS] ?? null,
      [PRODUCT_TIMELINESS_CATEGORY]:
        filteredProperties[PRODUCT_TIMELINESS_CATEGORY] ?? null,
      [PRODUCT_ACQUISITION_TYPE]:
        filteredProperties[PRODUCT_ACQUISITION_TYPE] ?? null,
      [PROCESSING_CONTEXT]: filteredProperties[PROCESSING_CONTEXT] ?? null,
      [PROCESSING_CORRECTION]:
        filteredProperties[PROCESSING_CORRECTION] ?? null,
      [PROCESSING_DATETIME]: filteredProperties[PROCESSING_DATETIME] ?? null,
      [PROCESSING_LINEAGE]: filteredProperties[PROCESSING_LINEAGE] ?? null,
      [PROCESSING_VERSION]: filteredProperties[PROCESSING_VERSION] ?? null,
      [INSTRUMENT]: filteredProperties[INSTRUMENT] ?? null,
      [INSTRUMENT_MODE]: filteredProperties[INSTRUMENT_MODE] ?? null,
      [POLARIZATIONS]: filteredProperties[POLARIZATIONS] ?? null,
      [VERSION]: filteredProperties[VERSION] ?? null,
      [COUPLING_MODES]: filteredProperties[COUPLING_MODES] ?? null,
      [SAR_FREQUENCY_BAND]: filteredProperties[SAR_FREQUENCY_BAND] ?? null,
      [SAR_CENTER_FREQUENCY]: filteredProperties[SAR_CENTER_FREQUENCY] ?? null,
      [SAR_RESOLUTION_RANGE]: filteredProperties[SAR_RESOLUTION_RANGE] ?? null,
      [SAR_RESOLUTION_AZIMUTH]:
        filteredProperties[SAR_RESOLUTION_AZIMUTH] ?? null,
      [SAR_PIXEL_SPACING_RANGE]:
        filteredProperties[SAR_PIXEL_SPACING_RANGE] ?? null,
      [SAR_PIXEL_SPACING_AZIMUTH]:
        filteredProperties[SAR_PIXEL_SPACING_AZIMUTH] ?? null,
      [SAR_LOOKS_RANGE]: filteredProperties[SAR_LOOKS_RANGE] ?? null,
      [SAR_LOOKS_AZIMUTH]: filteredProperties[SAR_LOOKS_AZIMUTH] ?? null,
      [SAR_LOOKS_EQUIVALENT_NUMBER]:
        filteredProperties[SAR_LOOKS_EQUIVALENT_NUMBER] ?? null,
      [SAR_OBSERVATION_DIRECTION]:
        filteredProperties[SAR_OBSERVATION_DIRECTION] ?? null,
      [SAR_RELATIVE_BURST]: filteredProperties[SAR_RELATIVE_BURST] ?? null,
      [SAR_BEAM_IDS]: filteredProperties[SAR_BEAM_IDS] ?? null,
      [SENSOR_ANGLE]: filteredProperties[SENSOR_ANGLE] ?? null,
      [SENSOR_PITCH]: filteredProperties[SENSOR_PITCH] ?? null,
      [SENSOR_ROLL]: filteredProperties[SENSOR_ROLL] ?? null,
      [RASTER_SPATIAL_RESOLUTION]:
        filteredProperties[RASTER_SPATIAL_RESOLUTION] ?? null,
      [TEMPORAL_RESOLUTION]: filteredProperties[TEMPORAL_RESOLUTION] ?? null,
      [RESOLUTION_MODE]: filteredProperties[RESOLUTION_MODE] ?? null,
      [MISSION_TAKE_ID]: filteredProperties[MISSION_TAKE_ID] ?? null,
      [GERALD_NAME]: filteredProperties[GERALD_NAME] ?? null,
      [ACQUISITION_STATION]: filteredProperties[ACQUISITION_STATION] ?? null,
      [CLASSIFICATION]: filteredProperties[CLASSIFICATION] ?? null,
      [THEMES]: filteredProperties[THEMES] ?? null,
      [PROCESSING_FACILITY]: filteredProperties[PROCESSING_FACILITY] ?? null,
      [MISSION]: filteredProperties[MISSION] ?? null,
      [PROCESSING_WEATHERDATA]:
        filteredProperties[PROCESSING_WEATHERDATA] ?? null,
      [PRODUCT_VALIDITY]: filteredProperties[PRODUCT_VALIDITY] ?? null,
      [END_POINT_URL]: filteredProperties[END_POINT_URL] ?? null,
      [DATASET]: filteredProperties[DATASET] ?? null,
      [VISIBILITY]: filteredProperties[VISIBILITY] ?? null,
      [START_DATE_TIME]: filteredProperties[START_DATE_TIME] ?? null,
      [END_DATE_TIME]: filteredProperties[END_DATE_TIME] ?? null,
    },
    geometry: {
      multipolygon: filteredProperties[NO_GEOMETRY]
        ? 'No geometry'
        : stacData.geometry?.type &&
          stacData.geometry?.type.toUpperCase() +
            '(((' +
            stacData.geometry?.coordinates.flat().toString() +
            ')))',
    },
    keywords: filteredProperties[KEYWORDS] ?? [],
    location: filteredProperties[NO_GEOMETRY]
      ? 'No geometry'
      : filteredProperties[POLITICAL],
  };
};
